/**
 * Client-side error logger.
 *
 * Intercepts HTTP errors, captures structured info, and ships to the backend
 * POST /error-logs/frontend endpoint.  Never throws — errors here are silent.
 */

const BATCH_INTERVAL = 5000;   // flush every 5s
const MAX_QUEUE = 20;          // cap queue to avoid memory bloat

let _authAxios = null;         // injected by AuthContext after login
const _queue = [];
let _flushTimer = null;

/** Called once by AuthContext when the axios instance is ready. */
export function initErrorLogger(authAxiosInstance) {
  _authAxios = authAxiosInstance;
  _startFlushLoop();
}

/** Called by AuthContext on logout to clear state. */
export function resetErrorLogger() {
  _authAxios = null;
  _queue.length = 0;
}

/**
 * Enqueue a structured error record.
 * @param {Object} opts
 */
export function captureError({
  route,
  method = "GET",
  statusCode,
  message,
  pageUrl = window.location.href,
  details = {},
}) {
  if (!statusCode && !message) return;

  // Deduplicate: skip if last queued item is identical
  const last = _queue[_queue.length - 1];
  if (last && last.route === route && last.status_code === statusCode) return;

  if (_queue.length >= MAX_QUEUE) _queue.shift(); // drop oldest if full
  _queue.push({
    route: route || window.location.pathname,
    method: method.toUpperCase(),
    status_code: statusCode || 0,
    message: String(message || "").slice(0, 400),
    page_url: pageUrl,
    details,
    timestamp: new Date().toISOString(),
  });
}

/** Flush queue immediately (e.g. on page unload). */
export async function flushErrors() {
  if (!_authAxios || _queue.length === 0) return;
  const batch = _queue.splice(0, _queue.length);
  for (const item of batch) {
    try {
      await _authAxios.post("/error-logs/frontend", item);
    } catch {
      // silently discard — never block the UI
    }
  }
}

function _startFlushLoop() {
  if (_flushTimer) return;
  _flushTimer = setInterval(flushErrors, BATCH_INTERVAL);
}
